import type { operations } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '@/lib/api'

/**
 * A mutation whose request body is the current company, read at CALL time
 * rather than at hook-creation time.
 *
 * TanStack Query hooks capture their closure at render; a screen whose `me`
 * query resolves after first render would otherwise send the mutation with an
 * empty X-Company-Id. The ref holds the latest companyId between renders.
 */
function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

/**
 * One mapping row, derived from the `accountMapping.index` response. `key` is
 * an `AccountMappingKey` value, e.g. `accounts_receivable`.
 */
export type AccountMapping =
  operations['accountMapping.index']['responses'][200]['content']['application/json']['data'][number]

/**
 * Which account each mapping key points at (design §4.2).
 *
 * The mappings are how the posting engine resolves "the receivables account"
 * without hard-coding a code. They were seeder-only until now — no endpoint,
 * no screen — so a company could not see them, let alone move one.
 */
export function useAccountMappings(
  companyId: string | null,
  options?: Omit<UseQueryOptions<AccountMapping[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<AccountMapping[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'account-mappings'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<AccountMapping[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/account-mappings', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load mappings')
      return unwrap<AccountMapping[]>(response.data ?? {}, 'account-mappings') ?? []
    },
  })
}

export function useSetAccountMapping(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<AccountMapping, Error, { key: string; accountId: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<AccountMapping, Error, { key: string; accountId: string }>({
    ...options,
    mutationFn: async ({ key, accountId }) => {
      const response = await api.PUT('/accounting/account-mappings/{key}', {
        params: { path: { key } },
        headers: { 'X-Company-Id': currentCompany() },
        body: { account_id: accountId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to set the mapping')
      return unwrap<AccountMapping>(response.data ?? {}, 'account-mappings')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] })
      options?.onSuccess?.(...args)
    },
  })
}
